import React, { useEffect } from 'react';
import classNames from 'classnames';
import Swal from 'sweetalert2';
import moment from 'moment';
import { padStart } from 'lodash';
import { format } from 'date-fns';

export default function DatePicker({
  mondayFirst = false,
  offerDate,
  offerAnytime,
  openHouseDays,
  pickDate,
  pickPast,
  unavailableDays,
  unavailableAfter,
  updateFn,
  originalDate,
  comingSoonDate,
  serverDate, // API fetched date passed as prop instead
  updateState,
  state,
  // Angular
  $rootScope,
}) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const days = [
    { long: 'Sunday', short: 'Sun' },
    { long: 'Monday', short: 'Mon' },
    { long: 'Tuesday', short: 'Tue' },
    { long: 'Wednesday', short: 'Wed' },
    { long: 'Thursday', short: 'Thu' },
    { long: 'Friday', short: 'Fri' },
    { long: 'Saturday', short: 'Sat' },
  ];

  useEffect(() => {
    if (serverDate) {
      updateState(draft => {
        const todayDate = new Date(serverDate);
        const initialDate = comingSoonDate || todayDate;
        draft.localdate = originalDate ? new Date(originalDate) : initialDate;
        draft.localdate.setMinutes(
          Math.round(draft.localdate.getMinutes() / 60) * 30,
        );
        draft.today = todayDate;
      });
      initializeDate();
    }
  }, [serverDate]);

  useEffect(() => {
    getDaysInMonth();
  }, [state.currentViewDate]);

  useEffect(() => {
    updateDate();
  }, [state.selecteddate]);

  const isAdmin = () => $rootScope.auth && $rootScope.auth.isAdmin;

  function getDaysInMonth() {
    if (state.currentViewDate && state.today) {
      const month = state.currentViewDate.getMonth();
      const date = new Date(state.currentViewDate.getFullYear(), month, 1);
      const daysInMonth = [];
      while (date.getMonth() === month) {
        let showday = true;
        let inPast = date < state.today;
        if (inPast && !pickPast) {
          showday = false;
        }

        let inComingSoonPeriod = !inPast && date < comingSoonDate;

        if (
          state.today.getDate() === date.getDate() &&
          state.today.getYear() === date.getYear() &&
          state.today.getMonth() === date.getMonth()
        ) {
          // reaches here is the date is the current day
          showday = true;
          inPast = false;
          inComingSoonPeriod = state.today < comingSoonDate;
        }

        const day = new Date(date);
        const dayname = day.getDay();
        const daydate = day.getDate();

        let selected;
        if (date.getMonth() === state.selecteddate.getMonth()) {
          selected =
            date.getDate() === state.selecteddate.getDate() &&
            date.getFullYear() &&
            state.selecteddate.getFullYear();
        }
        daysInMonth.push({
          dayname,
          daydate,
          showday,
          inPast,
          inComingSoonPeriod,
          selected,
        });
        date.setDate(date.getDate() + 1);
      }

      updateState(draft => {
        draft.currentMonthName = monthNames[draft.currentViewDate.getMonth()];
        draft.month = daysInMonth;
      });
    }
  }

  function initializeDate() {
    updateState(draft => {
      draft.currentViewDate = new Date(draft.localdate);
      draft.selecteddate = new Date(draft.localdate);
      draft.currentMonthName = monthNames[draft.currentViewDate.getMonth()];
    });
    getDaysInMonth();
  }

  // Takes selected time and date and combines them into a date object
  function getDateAndTime(localdate) {
    return new Date(
      localdate.getFullYear(),
      localdate.getMonth(),
      localdate.getDate(),
      0,
      0,
    );
  }

  const getDateStr = day => {
    const date = new Date(
      state.currentViewDate.getFullYear(),
      state.currentViewDate.getMonth(),
      day.daydate,
    );
    return moment(date).format('YYYY-MM-DD');
  };

  const isDayUnavailableAfter = day => unavailableAfter
      ? unavailableAfter.getTime() <=
          new Date(
            state.currentViewDate.getFullYear(),
            state.currentViewDate.getMonth(),
            day.daydate,
          ).getTime()
      : false;

  const isDayUnavailable = day => {
    const dateStr = getDateStr(day);
    const dateAfter = isDayUnavailableAfter(day);

    return (
      !unavailableDays || unavailableDays.indexOf(dateStr) > -1 || dateAfter
    );
  };

  const dayHasOpenHouse = day => {
    const dateStr = getDateStr(day);
    return openHouseDays && openHouseDays.indexOf(dateStr) > -1;
  };

  function updateDate(date) {
    if (date) {
      updateFn(getDateAndTime(date));
    } else if (state.localdate) {
      const newdate = getDateAndTime(state.localdate);
      updateFn(newdate);
    }
  }

  const selectDate = (day, index) => {
    if (day.inPast && !pickPast) return;
    if (isDayUnavailableAfter(day)) {
      const maxDays =
        moment(unavailableAfter)
          .startOf('day')
          .diff(moment().startOf('day'), 'days') + 1;
      Swal.fire({
        type: 'warning',
        title: 'SENTRILOCK APPOINTMENT',
        text: `You can only book appointments on this listing a maximum of ${maxDays} days from the showing date.`,
      });
      return;
    }

    if (pickDate === true && day.showday) {
      Object.keys(state.month).forEach(number => {
        const item = state.month[number];
        if (item.selected === true) {
          updateState(draft => {
            draft.month[number].selected = false;
          });
        }
      });
      updateState(draft => {
        draft.month[index].selected = true;
        draft.selectedDay = days[day.dayname].long;
        draft.localdate = new Date(
          draft.currentViewDate.getFullYear(),
          draft.currentViewDate.getMonth(),
          day.daydate,
        );
      });

      initializeDate();
      updateDate(
        new Date(
          state.currentViewDate.getFullYear(),
          state.currentViewDate.getMonth(),
          day.daydate,
        ),
      );
    }
  };

  const moveForward = () => {
    updateState(draft => {
      let month = draft.currentViewDate.getMonth() + 1;
      let year = draft.currentViewDate.getFullYear();
      if (month === 12) {
        year += 1;
        month = 0;
      }
      draft.currentViewDate = new Date(year, month, 1);
    });
  };

  const moveBack = () => {
    updateState(draft => {
      let month = draft.currentViewDate.getMonth() - 1;
      let year = draft.currentViewDate.getFullYear();
      if (month === -1) {
        year -= 1;
        month = 11;
      }
      draft.currentViewDate = new Date(year, month, 1);
    });
  };

  const calcOffset = (day, index) => {
    if (index === 0) {
      let offset = `${day.dayname * 14.2857142}%`;
      if (mondayFirst === true) {
        offset = `${(day.dayname - 1) * 14.2857142}%`;
      }
      return offset;
    }
  };

  const isOfferDate = day => (
      getDateStr(day).trim() === (offerDate && offerDate.trim()) &&
      !offerAnytime
    );

  return (
    <div className="datepicker-booking">
      <div className="datepicker">
        <div className="datepicker-calendar">
          <div className="calendar-header">
            {pickDate && (
              <button
                data-cy="previous-month-button"
                className="goback"
                disabled={!state.currentViewDate}
                onClick={() => moveBack()}
              >
                <svg width="30" height="30">
                  <path
                    fill="none"
                    stroke="#354a5e"
                    strokeWidth="3"
                    d="M19,6 l-9,9 l9,9"
                  />
                </svg>
              </button>
            )}
            <div className="current-month-container">
              {state.currentViewDate && state.currentViewDate.getFullYear()}{' '}
              {state.currentMonthName}
            </div>
            {pickDate && (
              <button
                data-cy="next-month-button"
                className="goforward"
                disabled={!state.currentViewDate}
                onClick={() => moveForward()}
              >
                <svg width="30" height="30">
                  <path
                    fill="none"
                    stroke="#354a5e"
                    strokeWidth="3"
                    d="M11,6 l9,9 l-9,9"
                  />
                </svg>
              </button>
            )}
          </div>
          {/* Shows day names ie sun, mon, etc. */}
          <div className="calendar-day-header">
            {days.map(day => (
              <span className="day-label" key={day.short}>
                {day.short}
              </span>
            ))}
          </div>

          {/* Date numbers */}
          <div className={`calendar-grid ${pickDate}`}>
            {state.month &&
              state.month.map((day, index) => (
                <div
                  key={day.daydate}
                  className={classNames('datecontainer', {
                    'no-hover': day.inPast && !day.showday,
                    'greyed-out':
                      (day.inPast || day.inComingSoonPeriod) && day.showday,
                  })}
                  style={{ marginLeft: calcOffset(day, index) }}
                >
                  <div
                    data-cy="select-date"
                    data-cy-metadata={`${format(
                      state.currentViewDate,
                      'yyyy-MM',
                    )}-${padStart(day.daydate, 2, 0)}`}
                    className={classNames('datenumber', {
                      'day-selected': day.selected,
                      'day-openhouse': dayHasOpenHouse(day),
                      'day-offerdate': isOfferDate(day),
                      'day-unavailable': isDayUnavailable(day),
                      'day-unavailable-after':
                        isAdmin() && isDayUnavailableAfter(day),
                    })}
                    onClick={() => selectDate(day, index)}
                  >
                    {day.daydate}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
